import { createFileRoute, Link } from "@tanstack/react-router";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import {
  buttonClasses,
  Card,
  ContactBlock,
  DeviceRow,
  EmptyState,
  LetterRow,
  PropertyRow,
  RequestCard,
  RowList,
  Skeleton,
} from "@/components/ui";
import { useActiveMembership } from "@/hooks/use-customer-context";
import { useCustomerNotices } from "@/hooks/use-customer-notices";
import { useHomeStanding } from "@/hooks/use-home-standing";
import { formatLongDate } from "@/lib/dates";
import {
  deviceLink,
  letterLink,
  lettersLink,
  needsTestDevicesLink,
  propertiesLink,
  propertyLink,
  requestLink,
  requestsLink,
  requestTestLink,
} from "@/lib/destinations";
import { deviceLabel } from "@/lib/device-label";
import { type Standing } from "@/lib/home-standing";
import { brandingFromMembership } from "@/lib/portal-memory";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];

export const Route = createFileRoute("/_customer/home/")({
  component: HomePage,
});

const ATTENTION_ROWS = 5;
const PHONE_ATTENTION_ROWS = 3;
const RECENT_LETTERS = 3;

const GRID = "grid items-start gap-4.5 lg:grid-cols-[1fr_340px] lg:gap-6";
const COLUMN = "flex min-w-0 flex-col gap-4.5 lg:gap-5.5";
const SECTION_HEAD = "mb-2.5 flex items-center justify-between gap-3";
const SECTION_TITLE = "text-lg font-semibold tracking-[-0.01em] text-ink";
// A 44px target drawn in the header's own height.
const SECTION_LINK =
  "-my-2.5 inline-flex min-h-11 shrink-0 items-center text-xs font-medium text-brand-fg underline-offset-2 hover:underline";

// Below lg the list shows its first three rows: the rest are hidden, and the
// third closes the surface the way the last row does.
function attentionRowClass(index: number, shown: number): string | undefined {
  if (index >= PHONE_ATTENTION_ROWS) return "max-lg:hidden";
  if (index === PHONE_ATTENTION_ROWS - 1 && shown > PHONE_ATTENTION_ROWS) {
    return "max-lg:border-b-0 max-lg:rounded-b-[calc(var(--radius-card)-1px)]";
  }
  return undefined;
}

function statementOf(standing: Standing, t: TFunction): string {
  switch (standing.variant) {
    case "needs_test":
      return standing.total === 1
        ? t("home.statement.needsTestSingle")
        : t("home.statement.needsTest", { count: standing.needsTest, total: standing.total });
    case "all_clear":
      return t("home.statement.allClear", { count: standing.total });
    case "no_devices":
      return t("home.statement.empty");
  }
}

function subStatementOf(standing: Standing, t: TFunction, language: string, org: string): string {
  if (standing.variant === "needs_test") {
    const { failed, pastDue, neverTested } = standing.parts;
    const parts = [
      failed > 0 ? t("home.sub.failed", { count: failed }) : null,
      pastDue > 0 ? t("home.sub.pastDue", { count: pastDue }) : null,
      neverTested > 0 ? t("home.sub.neverTested", { count: neverTested }) : null,
    ].filter((part): part is string => part !== null);
    return new Intl.ListFormat(language, { type: "conjunction" }).format(parts);
  }
  if (standing.variant === "all_clear") {
    return standing.nextDue
      ? t("home.sub.nextDue", { date: formatLongDate(standing.nextDue, language) })
      : t("home.sub.noneDue");
  }
  return t("home.sub.empty", { org });
}

function nameOf(installation: CustomerInstallation | undefined): string | undefined {
  if (!installation) return undefined;
  const name = deviceLabel({
    sizeLabel: installation.size_label,
    assemblyType: installation.assembly_type,
    manufacturerLabel: installation.manufacturer_label,
    modelLabel: installation.model_label,
  });
  return name || undefined;
}

// The loaded page's shape in static blocks: the statement and its action, a
// list, then the side column's cards.
const LOADING = (
  <div className={GRID}>
    <div className={COLUMN}>
      <div>
        <Skeleton height={31} className="max-w-[20ch]" />
        <Skeleton height={18} className="mt-3 max-w-[34ch]" />
        <Skeleton width={160} height={44} className="mt-4 rounded-control" />
      </div>
      <div>
        <Skeleton width={140} height={20} className="mb-2.5" />
        <div className="divide-y divide-line rounded-card border border-line bg-surface">
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-3 px-3.5 py-3 lg:px-4.5">
              <Skeleton width={44} height={36} className="shrink-0 rounded-control" />
              <div className="flex-1 space-y-2">
                <Skeleton width="60%" />
                <Skeleton width="40%" height={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className={COLUMN}>
      <Skeleton variant="card" height={140} />
      <Skeleton variant="card" height={160} />
    </div>
  </div>
);

function HomePage() {
  const { t, i18n } = useTranslation("customer");
  const active = useActiveMembership();
  const { standing, facilitiesById, installationsById, isError, refetch } = useHomeStanding();
  const notices = useCustomerNotices({
    ...(active ? { account_id: active.account_id } : {}),
    sort_by: "generated_at",
    sort_dir: "desc",
    limit: RECENT_LETTERS,
  });

  if (!standing) {
    if (!isError) return LOADING;
    return (
      <Card>
        <EmptyState
          title={t("home.error.title")}
          description={t("home.error.body")}
          actionLabel={t("home.error.retry")}
          onAction={refetch}
        />
      </Card>
    );
  }

  const org = active?.org_name ?? "";
  const attention = standing.needsAttention.slice(0, ATTENTION_ROWS);
  const onlyDevice = standing.needsAttention.length === 1 ? standing.needsAttention[0] : undefined;
  const { properties, openRequests } = standing;
  const onlyProperty = properties.length === 1 ? properties[0] : undefined;
  const letters = notices.data?.items ?? [];
  // A failed read with no letters to show says so; one that failed after an
  // earlier answer keeps showing that answer.
  const lettersFailed = notices.isError && letters.length === 0;

  return (
    <div className={GRID}>
      <div className={COLUMN}>
        <div>
          <h1 className="max-w-[20ch] text-3xl font-semibold tracking-[-0.015em] text-balance text-ink lg:text-4xl">
            {statementOf(standing, t)}
          </h1>
          <p className="mt-2 max-w-[48ch] text-base text-muted">
            {subStatementOf(standing, t, i18n.language, org)}
          </p>
          <div className="mt-4">
            <Link
              {...requestTestLink(standing.preselect)}
              className={buttonClasses(standing.variant === "needs_test" ? "primary" : "secondary")}
            >
              {t("home.requestTest")}
            </Link>
          </div>
        </div>

        {attention.length > 0 && (
          <section>
            <div className={SECTION_HEAD}>
              <h2 className={SECTION_TITLE}>{t("home.needsAttention.title")}</h2>
              <Link
                {...(onlyDevice ? deviceLink(onlyDevice.installation.id) : needsTestDevicesLink())}
                className={SECTION_LINK}
              >
                {t("home.needsAttention.all", { count: standing.needsAttention.length })}
              </Link>
            </div>
            <RowList>
              {attention.map((item, index) => (
                <DeviceRow
                  key={item.installation.id}
                  className={attentionRowClass(index, attention.length)}
                  installation={item.installation}
                  link={deviceLink(item.installation.id)}
                  propertyName={facilitiesById.get(item.installation.facility_id)?.name}
                  openRequest={item.openRequest}
                  timezone={active?.org_timezone}
                />
              ))}
            </RowList>
          </section>
        )}

        <section>
          <div className={SECTION_HEAD}>
            <h2 className={SECTION_TITLE}>{t("home.properties.title")}</h2>
            {properties.length > 0 && (
              <Link
                {...(onlyProperty ? propertyLink(onlyProperty.facility.id) : propertiesLink())}
                className={SECTION_LINK}
              >
                {t("home.properties.all", { count: properties.length })}
              </Link>
            )}
          </div>
          {properties.length > 0 ? (
            <RowList>
              {properties.map((property) => (
                <PropertyRow
                  key={property.facility.id}
                  standing={property}
                  link={propertyLink(property.facility.id)}
                />
              ))}
            </RowList>
          ) : (
            <Card>
              <EmptyState variant="inline" title={t("home.properties.empty", { org })} />
            </Card>
          )}
        </section>
      </div>

      <div className={COLUMN}>
        <section>
          <div className={SECTION_HEAD}>
            <h2 className={SECTION_TITLE}>{t("home.inMotion.requests")}</h2>
            <Link {...requestsLink()} className={SECTION_LINK}>
              {t("home.inMotion.allRequests")}
            </Link>
          </div>
          {openRequests.length > 0 ? (
            <ul className="flex flex-col gap-2.5">
              {openRequests.map((request) => (
                <li key={request.id}>
                  <RequestCard
                    request={request}
                    link={requestLink(request)}
                    deviceLabel={nameOf(
                      installationsById.get(request.device_installation_id ?? ""),
                    )}
                    propertyName={facilitiesById.get(request.facility_id)?.name}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <Card>
              <p className="text-sm text-muted">{t("home.inMotion.noRequests")}</p>
            </Card>
          )}
        </section>

        {(letters.length > 0 || lettersFailed) && (
          <section>
            <div className={SECTION_HEAD}>
              <h2 className={SECTION_TITLE}>{t("home.inMotion.letters")}</h2>
              <Link {...lettersLink()} className={SECTION_LINK}>
                {t("home.inMotion.allLetters")}
              </Link>
            </div>
            {lettersFailed ? (
              <Card>
                <EmptyState
                  variant="inline"
                  title={t("home.letters.loadFailed")}
                  actionLabel={t("home.error.retry")}
                  onAction={() => void notices.refetch()}
                />
              </Card>
            ) : (
              <RowList>
                {letters.map((notice) => (
                  <LetterRow
                    key={notice.id}
                    notice={notice}
                    link={letterLink(notice.id)}
                    orgName={org}
                    timezone={active?.org_timezone}
                    contextLabel={
                      facilitiesById.get(notice.facility_id ?? "")?.name ??
                      nameOf(installationsById.get(notice.device_installation_id ?? ""))
                    }
                  />
                ))}
              </RowList>
            )}
          </section>
        )}

        {active && <ContactBlock branding={brandingFromMembership(active)} />}
      </div>
    </div>
  );
}
